import styled from "@emotion/styled";
import { getDatabase, ref, child, get, set } from "firebase/database";
import { sha256 } from "js-sha256";
import { useCallback, useEffect, useRef, useState } from "react";
import { fireBaseApp } from "../db/db";
import { generateString, removeComment, setComment } from "../utils/utils";
import { Button } from "./Button";
import { FadeInContent } from "./FadeInContent";
import { Italic } from "./Italic";

type Comment = {
  name: string;
  pwd: string;
  text: string;
  key: string;
  likes: number;
  timestamp: number;
};

interface CommentControlProps {
  comment: Comment;
  deleteComment: (key: string, pwd: string) => void;
  editComment: (key: string) => void;
  update: () => void;
  onLike: (key: string) => void;
  isTop3?: boolean;
}

const CommentTop = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  font-size: 14px;
  margin-bottom: 10px;
  padding-right: 5px;

  i {
    font-style: italic;
    font-size: 12px;
  }
`;

const HeartWrapper = styled.div`
  color: pink;
  cursor: pointer;
`;

const CommentBody = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  border-radius: 10px;
  border: 1px solid #eaeaea;
  min-height: 80px;
  padding: 10px;
  text-align: left;
  font-size: 14px;
  width: 100%;
`;

const DeleteButton = styled.div`
  position: absolute;
  right: 8px;
  bottom: 8px;
  opacity: 0.5;
  cursor: pointer;
`;

const CommentsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  width: 100%;
`;

const getComments = async () => {
  const snapshot = await get(child(ref(getDatabase(fireBaseApp)), "comments"));
  const comments = snapshot.val();
  return comments;
};

const CommentControlContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 10px;
  gap: 10px;
  width: 100%;
`;

const Top3CommentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  position: relative;
  margin-top: 10px;
  padding-top: 20px;
  border: 1px solid #eaeaea;
  width: 90%;
  border-radius: 10px;
`;

const Top3Desc = styled.div`
  position: absolute;
  top: -14px;
  left: 10px;
  font-size: 12px;
  border: 1px solid #eaeaea;
  border-radius: 5px;
  padding: 5px;
  background-color: white;
`;

const CommentControl = ({
  comment,
  deleteComment,
  update,
  onLike,
  isTop3,
}: CommentControlProps) => {
  const [liked, setLiked] = useState(false);
  const [internalLike, setInternalLike] = useState(comment.likes);

  const checkVoted = useCallback(() => {
    const voted = localStorage.getItem(`votedTo${comment.key}`);
    if (voted) {
      setLiked(true);
    }
  }, [comment]);

  useEffect(() => {
    checkVoted();
  }, [checkVoted]);

  const setLocalStorage = useCallback(() => {
    localStorage.setItem(`votedTo${comment.key}`, "true");
    setLiked(true);
  }, [comment]);

  return (
    <CommentControlContainer>
      <CommentBody>
        <CommentTop>
          <div>
            <i>from</i> {comment.name}{" "}
          </div>
          <HeartWrapper
            onClick={() => {
              if (!liked) {
                onLike(comment.key);
                setInternalLike(internalLike + 1);
                setLocalStorage();
              }
            }}
          >
            {liked ? "♥" : "♡"}
            {internalLike}
          </HeartWrapper>
        </CommentTop>
        <div>{comment.text}</div>
        <DeleteButton
          onClick={() => {
            const pwd = prompt("비밀번호를 입력해주세요");
            if (pwd && sha256(pwd) === String(comment.pwd )) {
              deleteComment(comment.key, pwd);
            } else {
              alert("비밀번호가 틀렸습니다.");
            }
          }}
        >
          x
        </DeleteButton>
      </CommentBody>
    </CommentControlContainer>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-top: 1px solid #eaeaea;
  padding-top: 20px;
  gap: 10px;
  width: 100%;
  padding-bottom: 20px;
`;

const InputBox = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
  height: 50px;
  border-bottom: 1px solid #eaeaea;
  width: 100%;
`;

const InputTitle = styled.div`
  width: 100px;
  text-align: left;
`;

const Input = styled.input`
  border: unset;
  width: 100%;
 
  :focus {
    outline: none;
  }
`;

const PwdInput = styled(Input)`
 -webkit-text-security: disc;
  -moz-webkit-text-security: disc;
  -moz-text-security: disc;

`

const InputForm = styled.form`
  width: 100%;
  padding-left: 40px;
  padding-right: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
`;

export const GuestBook = () => {
  const [comments, setComments] = useState<Comment[]>([]);
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const getCommentList = useCallback(async () => {
    const comments = await getComments();
    if (comments) {
      setComments(Object.values(comments));
    }
  }, []);

  const [numberToShow, setNumberToShow] = useState(6);

  useEffect(() => {
    getCommentList();
    // if (passwordInputRef.current) {
    //   passwordInputRef.current.type = "password";
    // }
  }, []);

  const writeComment = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // @ts-ignore
    const name = e.currentTarget.name2.value;
    const pwd = e.currentTarget.pwd.value;
    const text = e.currentTarget.text.value;

    // @ts-ignore
    e.currentTarget.name2.value = "";
    e.currentTarget.pwd.value = "";
    e.currentTarget.text.value = "";

    if (!name || !pwd || !text) {
      return;
    }

    const cryptoPwd = sha256(pwd)

    const key = generateString(name, 8);
    await setComment(
      key,
      text,
      name,
      cryptoPwd,
      0,
      Date.now()
    )

    const commentObj = {
      key,
      name,
      pwd: cryptoPwd,
      text,
      likes: 0,
      timestamp: Date.now(),
    };
    setComments([commentObj, ...comments]);

    alert("축하해줘서 고맙습니다! ❤️");
  };

  const deleteComment = async (key: string, pwd: string) => {
    const cryptoPwd = sha256(pwd)
    // console.log(cryptoPwd)
    await removeComment(key, cryptoPwd);
    const filteredComments = comments.filter((comment) => comment.key !== key);
    setComments(filteredComments);
    alert("삭제 되었습니다.");
  };

  // get key as props and update likes
  const onLike = async (key: string) => {
    const commentRes = await getComments();
    const comments = Object.values(commentRes) as Comment[];
    const comment = comments.find((comment) => comment.key === key);
    if (comment) {
      await setComment(
      key,
      comment.text,
      comment.name,
      comment.pwd,
      comment.likes + 1,
      comment.timestamp
    )
      await new Promise((resolve) => setTimeout(resolve, 1000));
      getCommentList();
    }
  };

  return (
    <Container>
      <FadeInContent
        style={{
          marginBottom: "20px",
        }}
      >
        <Italic>- Guest Book -</Italic>
      </FadeInContent>
      {/* <FadeInContent>
        <b>축하의 메시지를 쏴리질러~~</b>
      </FadeInContent> */}

      {comments &&
            comments.length > 3 && <Top3CommentWrapper>
        <Top3Desc>❤️ Most Loved ❤️</Top3Desc>
        <CommentsContainer>
          {comments &&
            comments
              .sort((a, b) => b.likes - a.likes)
              .map((comment, i) => {
                if (i <= 2) {
                  return (
                    <FadeInContent
                      key={comment.key}
                      style={{
                        width: "100%",
                      }}
                    >
                      <CommentControl
                      comment={comment}
                      deleteComment={deleteComment}
                      editComment={() => {}}
                      onLike={onLike}
                      update={getCommentList}
                    />

                    </FadeInContent>
                    
                  );
                }
                return null;
              })}
        </CommentsContainer>
      </Top3CommentWrapper>}
      <FadeInContent
        style={{
          width: "100%",
        }}
      >
        <InputForm
        onSubmit={(e) => {
          writeComment(e);
        }}
      >
        <InputBox>
          <InputTitle>이름</InputTitle>
          <Input type="text" name="name2" autoComplete="off" required />
        </InputBox>
        <InputBox>
          <InputTitle>비밀번호</InputTitle>
          <PwdInput
            ref={passwordInputRef}
            type="text"
            required
            name="pwd"
            autoComplete="off" 
          />
        </InputBox>
        <InputBox>
          <InputTitle>내용</InputTitle>
          <Input type="text" name="text" autoComplete="off" required />
        </InputBox>
        <Button style={{ width: "80%" }} as={"button"} type="submit">
          작성
        </Button>
      </InputForm>

      </FadeInContent>
      
      <CommentsContainer>
        {comments &&
          comments
            .sort((a, b) => b.timestamp - a.timestamp)
            .map((comment, i) => {
              if (i >= numberToShow) {
                return null;
              }
              return (
                <FadeInContent
                  style={{
                    width: "100%",
                  }}
                >
                  <CommentControl
                  key={comment.key}
                  comment={comment}
                  deleteComment={deleteComment}
                  editComment={() => {}}
                  onLike={onLike}
                  update={getCommentList}
                />

                </FadeInContent>
                
              );
            })}
      </CommentsContainer>
      {comments.length > numberToShow && (
        <FadeInContent 
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
          }}
        >
           <Button
          as={"button"}
          style={{ width: "80%" }}
          onClick={() => {
            setNumberToShow(numberToShow + 6);
          }}
        >
          더보기
        </Button>

        </FadeInContent>
       
      )}
    </Container>
  );
};
